import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Facebook, MessageCircle } from "lucide-react";
import SEO from "@/components/seo/SEO";
import { useContenido } from "@/context/ContenidoContext";
import { HORARIO_TEXTO } from "@/lib/horario";

export default function Contacto() {
    const { t } = useTranslation();
    const { config } = useContenido();
    const horarios = config?.horarios?.length ? config.horarios : HORARIO_TEXTO;
    const telefono = config?.telefono || "925 48 46 33";
    const telLink = config?.telefono_link || "+34925484633";
    const email = config?.email || "restaurantemuseoteneria@gmail.com";
    const facebook =
        config?.facebook_url ||
        "https://www.facebook.com/Restaurante-Museo-Tener%C3%ADa-386110368612753";
    const whatsapp = config?.whatsapp?.replace(/[^0-9]/g, "") || "34925484633";
    const direccion = config?.direccion || "Plaza del Mercado 3 · Ocaña · 45300 Toledo";
    const mapQuery =
        config?.google_maps_query ||
        "Restaurante Museo Tenería Plaza del Mercado 3 Ocaña";

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={t("contacto.seo.title")}
                description={t("contacto.seo.description")}
                keywords="contacto restaurante Ocaña, cómo llegar Tenería, dirección restaurante Ocaña, teléfono Tenería Ocaña, restaurante Plaza del Mercado Ocaña"
                path="/contacto"
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.contacto"), item: "/contacto" },
                ]}
            />
            <section className="max-w-5xl mx-auto px-6 text-center mb-20">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    className="roman-numeral"
                >
                    — {t("contacto.kicker")} —
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light text-[clamp(2.2rem,6vw,5.5rem)] leading-[0.95] text-ivory mt-8"
                >
                    {t("contacto.title1")} <span className="italic text-gold">{t("contacto.title2")}</span>
                </motion.h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 1 }}
                    className="line-gold mx-auto my-10 origin-left"
                />
            </section>

            <section className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20 mb-16">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="space-y-12"
                >
                    <div>
                        <p className="roman-numeral">— I · {t("contacto.address")} —</p>
                        <p className="font-display text-3xl md:text-4xl text-ivory leading-snug whitespace-pre-line mt-4">
                            {direccion}
                        </p>
                        <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-gold mt-5 link-underline font-editorial italic"
                            data-testid="contact-map-link"
                            data-cursor="hover"
                        >
                            <MapPin size={16} /> {t("contacto.howToReach")}
                        </a>
                    </div>

                    <div>
                        <p className="roman-numeral">— II · {t("contacto.schedule")} —</p>
                        <ul className="space-y-1 font-body text-base mt-4">
                            {horarios.map((h) => (
                                <li key={h.dia} className="flex justify-between gap-6">
                                    <span className="text-ivory">{h.dia}</span>
                                    <span
                                        className={
                                            h.cerrado ? "text-oxblood italic" : "text-stone"
                                        }
                                    >
                                        {h.horas}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="space-y-4">
                        <p className="roman-numeral">— III · {t("contacto.reach")} —</p>
                        <a
                            href={`tel:${telLink}`}
                            className="flex items-center gap-3 text-ivory hover:text-gold transition-colors mt-4"
                            data-testid="contact-phone"
                            data-cursor="hover"
                        >
                            <Phone size={16} className="text-gold" />
                            <span className="font-display italic text-2xl">{telefono}</span>
                        </a>
                        <a
                            href={`mailto:${email}`}
                            className="flex items-center gap-3 text-ivory hover:text-gold transition-colors"
                            data-testid="contact-email"
                            data-cursor="hover"
                        >
                            <Mail size={16} className="text-gold" />
                            <span className="font-body break-all">{email}</span>
                        </a>
                        <a
                            href={facebook}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 text-ivory hover:text-gold transition-colors"
                            data-testid="contact-facebook"
                            data-cursor="hover"
                        >
                            <Facebook size={16} className="text-gold" />
                            <span className="font-body">Facebook</span>
                        </a>
                        <a
                            href={`https://wa.me/${whatsapp}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 text-ivory hover:text-gold transition-colors"
                            data-testid="contact-whatsapp"
                            data-cursor="hover"
                        >
                            <MessageCircle size={16} className="text-gold" />
                            <span className="font-body">WhatsApp</span>
                        </a>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="aspect-square md:aspect-auto md:min-h-[600px] border border-gold/30"
                >
                    <iframe
                        title="Mapa Restaurante Museo Tenería"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                        width="100%"
                        height="100%"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        style={{ border: 0, filter: "grayscale(0.7) contrast(1.15) brightness(0.8) sepia(0.15)" }}
                        data-testid="contact-google-map"
                    />
                </motion.div>
            </section>
        </article>
    );
}
